'use client'

import { cn } from '@/lib/utils'
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from 'motion/react'
import { useEffect, useState, type CSSProperties } from 'react'
import Image, { getImageProps } from 'next/image'
import { preload } from 'react-dom'
import { EASE } from './animation-tokens'
import { CAPTURE_HEIGHT, CAPTURE_WIDTH, PhoneFrame, captureSrc } from './phone-frame'

type Tone = 'overdue' | 'soon' | 'info' | 'done'

const DOT: Record<Tone, string> = {
  overdue: 'bg-destructive',
  soon: 'bg-warning',
  info: 'bg-info',
  done: 'bg-primary',
}

interface Reminder {
  title: string
  body: string
  tone: Tone
  when?: string
  /** What the card turns into once the job is checked off on the phone. */
  done?: { title: string; body: string }
  /** Which side of the phone, how far from its center edge, and how far down. */
  side: 'left' | 'right'
  offset: number
  top: number
}

// The reminders Haven sends, arranged around the dashboard they come from.
const FOREGROUND: Reminder[] = [
  {
    title: 'Replace furnace filter',
    body: '6 days late. You have 1 filter left.',
    tone: 'overdue',
    when: 'now',
    done: { title: 'Furnace filter replaced', body: 'The next change is due Dec 23.' },
    side: 'left',
    offset: 204,
    top: 95,
  },
  { title: 'Backflush espresso machine', body: 'Due Thursday. Uses 1 cleaning tablet.', tone: 'soon', when: '9:00', side: 'left', offset: 224, top: 285 },
  { title: 'Washer warranty ends Oct 17', body: 'The receipt and manual are on file.', tone: 'info', when: 'Mon', side: 'right', offset: 204, top: 130 },
  { title: 'HVAC filter is out', body: '14x16x1. Reorder from Amazon?', tone: 'overdue', when: 'Sun', side: 'right', offset: 224, top: 320 },
]

// Quieter reminders further out, so the phone reads as the source of a steady stream.
const BACKGROUND: Reminder[] = [
  { title: 'Smoke detectors tested', body: 'Next check is due in March.', tone: 'done', side: 'left', offset: 320, top: 20 },
  { title: 'Clean dishwasher filter', body: 'Due today.', tone: 'soon', side: 'left', offset: 370, top: 190 },
  { title: 'Lubricate garage door', body: 'Due Saturday. 1 can of lube left.', tone: 'soon', side: 'left', offset: 340, top: 375 },
  { title: 'Dryer vent cleaned', body: 'Next one is due Oct 2027.', tone: 'done', side: 'right', offset: 320, top: 30 },
  { title: 'Change Civic oil', body: 'Due in 11 days.', tone: 'soon', side: 'right', offset: 370, top: 215 },
  { title: 'Mower warranty', body: 'Active until Apr 2027.', tone: 'info', side: 'right', offset: 340, top: 420 },
]

function ReminderCard({
  reminder,
  done = false,
  className,
  style,
}: {
  reminder: Reminder
  done?: boolean
  className?: string
  style?: CSSProperties
}) {
  const showDone = done && reminder.done
  const title = showDone ? reminder.done!.title : reminder.title
  const body = showDone ? reminder.done!.body : reminder.body
  return (
    <div
      className={cn(
        'flex w-80 items-start gap-3 rounded-2xl border bg-card px-4 py-3 text-left text-sm transition-colors duration-500',
        showDone ? 'border-primary/40' : 'border-border',
        className
      )}
      style={style}
    >
      <span
        aria-hidden
        className={cn('mt-1.5 size-2 shrink-0 rounded-full transition-colors duration-500', showDone ? DOT.done : DOT[reminder.tone])}
      />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={title}
          className="min-w-0 flex-1"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          <p className="font-medium text-foreground">{title}</p>
          <p className="text-muted-foreground">{body}</p>
        </motion.div>
      </AnimatePresence>
      {reminder.when && <span className="text-xs text-muted-foreground">{reminder.when}</span>}
    </div>
  )
}

const position = (r: Reminder): CSSProperties => ({
  [r.side === 'left' ? 'right' : 'left']: `calc(50% + ${r.offset}px)`,
  top: r.top,
})

const enter = (r: Reminder, delay: number) => ({
  initial: { opacity: 0, x: r.side === 'left' ? 16 : -16 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.4, delay, ease: EASE },
})

const CAPTURE_SIZES = '360px'

/**
 * Two real captures of the dashboard: before and right after the furnace filter
 * is marked done (the hero moves to today's dishwasher task, the overdue count
 * clears). Cross-fading them shows the app's actual next state.
 */
function DashboardCaptures({ done }: { done: boolean }) {
  // The first capture is the LCP image on phones. Preload only the copy for the visitor's color
  // scheme, at high priority. The images themselves are lazy, so React doesn't also preload
  // both copies, and the other theme's (display:none) never downloads. Someone whose chosen theme
  // differs from their system's still gets theirs, after layout rather than from the preload.
  // The "done" state isn't visible until 2.9s; it's in the viewport, so it loads after layout.
  for (const theme of ['light', 'dark'] as const) {
    const { props } = getImageProps({
      src: captureSrc('dashboard', theme),
      alt: '',
      width: CAPTURE_WIDTH,
      height: CAPTURE_HEIGHT,
      sizes: CAPTURE_SIZES,
    })
    preload(props.src, {
      as: 'image',
      imageSrcSet: props.srcSet,
      imageSizes: props.sizes,
      fetchPriority: 'high',
      media: `(prefers-color-scheme: ${theme})`,
    })
  }

  const capture = (name: 'dashboard' | 'dashboard-done', alt: string, lcp: boolean) =>
    (['light', 'dark'] as const).map((theme) => (
      <Image
        key={`${name}-${theme}`}
        src={captureSrc(name, theme)}
        alt={alt}
        width={CAPTURE_WIDTH}
        height={CAPTURE_HEIGHT}
        fetchPriority={lcp ? 'high' : undefined}
        sizes={CAPTURE_SIZES}
        className={cn('w-full', theme === 'light' ? 'block dark:hidden' : 'hidden dark:block')}
      />
    ))

  return (
    <>
      {capture(
        'dashboard',
        "Haven's dashboard: an overdue furnace filter gets marked done and the next task takes its place",
        true
      )}
      <motion.div
        aria-hidden
        className="absolute inset-0"
        initial={false}
        animate={{ opacity: done ? 1 : 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        {capture('dashboard-done', '', false)}
      </motion.div>
    </>
  )
}

// Hero timeline, in ms after mount: cards arrive, then the overdue job gets checked off.
const TAP_AT = 2600
const DONE_AT = 2900

export function HeroReminders() {
  const reduceMotion = useReducedMotion()
  const [phase, setPhase] = useState<'waiting' | 'tap' | 'done'>('waiting')

  useEffect(() => {
    const tap = window.setTimeout(() => setPhase('tap'), reduceMotion ? DONE_AT : TAP_AT)
    const done = window.setTimeout(() => setPhase('done'), DONE_AT)
    return () => {
      window.clearTimeout(tap)
      window.clearTimeout(done)
    }
  }, [reduceMotion])

  const done = phase === 'done'

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative mx-auto h-[600px] w-full overflow-hidden md:h-[640px]">
        {/* Warm light behind the phone, like late sun through a window. Drifts slowly; still under reduced motion. */}
        <div aria-hidden className="pointer-events-none absolute left-1/2 top-24 h-[480px] w-[720px] -translate-x-1/2">
          <div className="hero-light absolute left-[8%] top-0 size-[420px] rounded-full bg-[radial-gradient(closest-side,var(--color-warning),transparent)] opacity-35 dark:opacity-20" />
          <div className="hero-light-alt absolute right-[6%] top-16 size-[380px] rounded-full bg-[radial-gradient(closest-side,var(--color-primary),transparent)] opacity-30 dark:opacity-30" />
        </div>

        {/* Background layer fades out toward the screen edges */}
        <div
          aria-hidden
          className="absolute inset-0 hidden xl:block [mask-image:linear-gradient(90deg,transparent_0,#000_14%,#000_86%,transparent_100%)]"
        >
          {BACKGROUND.map((r, i) => (
            <motion.div key={r.title} className="absolute" style={position(r)} {...enter(r, 0.9 + i * 0.08)}>
              <ReminderCard reminder={r} className="scale-95 opacity-55" />
            </motion.div>
          ))}
        </div>

        <div className="absolute left-1/2 top-0 z-10 w-[330px] -translate-x-1/2 md:w-[360px]">
          {/* No entrance: the capture is the LCP image on phones. Painted mid-rise it would be
              clipped smaller than the "done" capture that fades in at 2.9s, and that late,
              larger paint would become the LCP. */}
          <div className="relative">
            {/* One sage pulse when the job is checked off: feedback, not decoration */}
            <AnimatePresence>
              {done && !reduceMotion && (
                <motion.div
                  aria-hidden
                  className="pointer-events-none absolute -inset-1 rounded-4xl ring-2 ring-primary/50"
                  initial={{ opacity: 0.9, scale: 1 }}
                  animate={{ opacity: 0, scale: 1.06 }}
                  transition={{ duration: 1.1, ease: 'easeOut' }}
                />
              )}
            </AnimatePresence>
            <PhoneFrame className="shadow-xl shadow-foreground/5">
              <DashboardCaptures done={done} />
              {/* Tap on "Mark done", which sits at 73pt x 162pt of the 390 x 844pt screen */}
              <AnimatePresence>
                {phase === 'tap' && (
                  <motion.span
                    aria-hidden
                    className="pointer-events-none absolute size-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground/20"
                    style={{ left: `${(73 / 390) * 100}%`, top: `${(162 / 844) * 100}%` }}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.4 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </AnimatePresence>
            </PhoneFrame>
          </div>
        </div>

        <div aria-hidden className="hidden xl:block">
          {FOREGROUND.map((r, i) => (
            <motion.div key={r.title} className="absolute z-20" style={position(r)} {...enter(r, 0.5 + i * 0.12)}>
              <ReminderCard reminder={r} done={done} className="shadow-lg shadow-foreground/5" />
            </motion.div>
          ))}
        </div>

        {/* Below 1280px there is no room beside the phone: one reminder overlaps it instead */}
        <motion.div
          aria-hidden
          className="absolute left-1/2 top-[360px] z-20 w-[88%] max-w-sm -translate-x-1/2 xl:hidden"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6, ease: EASE }}
        >
          <ReminderCard reminder={FOREGROUND[0]} done={done} className="w-full shadow-lg shadow-foreground/10" />
        </motion.div>

        <div aria-hidden className="absolute inset-x-0 bottom-0 z-30 h-24 bg-gradient-to-t from-background to-transparent" />
      </div>
    </MotionConfig>
  )
}
